import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Save, Shield, Lock, Clock, Database, UserX, FileText, Eye } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/utils/cn'

import { AuditLogTab } from './AuditLogTab'
import { BackupTab } from './BackupTab'
import { BlockedUsersTab } from './BlockedUsersTab'
import { SystemLogsTab } from './SystemLogsTab'
import { BehavioralSecurityTab } from './BehavioralSecurityTab'

interface SecuritySettings {
  minPasswordLength: number
  requireUppercase: boolean
  requireSpecial: boolean
  passwordExpirationDays: number
  maxLoginAttempts: number
  lockoutMinutes: number
  sessionTimeoutMinutes: number
  maxConcurrentSessions: number
}

// TODO: conectar à API
const defaultSettings: SecuritySettings = {
  minPasswordLength: 8,
  requireUppercase: true,
  requireSpecial: true,
  passwordExpirationDays: 90,
  maxLoginAttempts: 5,
  lockoutMinutes: 15,
  sessionTimeoutMinutes: 30,
  maxConcurrentSessions: 3,
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <button
      type='button'
      role='switch'
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center ${
        checked ? 'bg-[var(--brand-primary)]' : 'bg-[var(--glass-bg)] border border-[var(--glass-border)]'
      }`}
    >
      <span
        className={`absolute left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  )
}

function NumberField({
  label,
  value,
  onChange,
  suffix,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  suffix?: string
}) {
  return (
    <div className='flex flex-col gap-1.5'>
      <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
        {label}
      </label>
      <div className='flex items-center gap-2'>
        <input
          type='number'
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={0}
          className='w-24 bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-[13px] outline-none focus:border-[var(--brand-primary)] transition-colors min-h-[44px]'
        />
        {suffix && <span className='text-[var(--text-muted)] text-[13px]'>{suffix}</span>}
      </div>
    </div>
  )
}

export function SecurityTab() {
  const [subTab, setSubTab] = useState<'policies' | 'behavioral' | 'audit' | 'backup' | 'blocked' | 'logs'>('policies')
  const [settings, setSettings] = useState<SecuritySettings>(defaultSettings)

  const update = (partial: Partial<SecuritySettings>) => {
    setSettings((prev) => ({ ...prev, ...partial }))
  }

  const handleSave = () => {
    // TODO: PUT /api/settings/security
    toast.success('Configurações de segurança salvas!')
  }

  const subTabs = [
    { id: 'policies' as const, label: 'Políticas', icon: <Lock size={13} /> },
    { id: 'behavioral' as const, label: 'Comportamental', icon: <Eye size={13} /> },
    { id: 'audit' as const, label: 'Auditoria', icon: <Shield size={13} /> },
    { id: 'backup' as const, label: 'Backup', icon: <Database size={13} /> },
    { id: 'blocked' as const, label: 'Bloqueios', icon: <UserX size={13} /> },
    { id: 'logs' as const, label: 'Logs', icon: <FileText size={13} /> },
  ]

  return (
    <div className='space-y-4'>
      {/* Sub-tabs header card */}
      <div className='glass-card p-5'>
        <div className="flex items-center gap-1 p-1 bg-white/[0.04] rounded-lg border border-white/[0.06] w-fit overflow-x-auto">
          {subTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold whitespace-nowrap transition-all min-h-[44px]',
                subTab === tab.id
                  ? 'bg-accent text-white shadow-lg shadow-accent/20'
                  : 'text-text-muted hover:text-text-primary hover:bg-white/[0.06]'
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sub-tab Content */}
      {subTab === 'policies' && (
        <div className='glass-card space-y-6'>
          <h3 className='font-semibold text-[var(--font-color-h3)]' style={{ fontSize: 'var(--font-size-h3)' }}>
            Políticas de Segurança
          </h3>

          {/* Política de Senha */}
          <div className='space-y-4'>
            <div className='flex items-center gap-2'>
              <Lock size={16} className='text-[var(--brand-primary)]' />
              <h4 className='font-semibold text-[var(--text-primary)] text-[14px]'>Política de Senha</h4>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 pl-6'>
              <NumberField
                label='Mínimo de caracteres'
                value={settings.minPasswordLength}
                onChange={(v) => update({ minPasswordLength: v })}
              />
              <NumberField
                label='Expiração'
                value={settings.passwordExpirationDays}
                onChange={(v) => update({ passwordExpirationDays: v })}
                suffix='dias'
              />
              <div className='flex items-center justify-between sm:col-span-2'>
                <span className='font-medium text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                  Requer maiúscula
                </span>
                <Toggle checked={settings.requireUppercase} onChange={(v) => update({ requireUppercase: v })} />
              </div>
              <div className='flex items-center justify-between sm:col-span-2'>
                <span className='font-medium text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                  Requer caractere especial
                </span>
                <Toggle checked={settings.requireSpecial} onChange={(v) => update({ requireSpecial: v })} />
              </div>
            </div>
          </div>

          <div className='border-t border-[var(--glass-border)]' />

          {/* Bloqueio de Login */}
          <div className='space-y-4'>
            <div className='flex items-center gap-2'>
              <Shield size={16} className='text-[var(--brand-primary)]' />
              <h4 className='font-semibold text-[var(--text-primary)] text-[14px]'>Bloqueio de Login</h4>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 pl-6'>
              <NumberField
                label='Tentativas máximas'
                value={settings.maxLoginAttempts}
                onChange={(v) => update({ maxLoginAttempts: v })}
              />
              <NumberField
                label='Tempo de bloqueio'
                value={settings.lockoutMinutes}
                onChange={(v) => update({ lockoutMinutes: v })}
                suffix='minutos'
              />
            </div>
          </div>

          <div className='border-t border-[var(--glass-border)]' />

          {/* Sessão */}
          <div className='space-y-4'>
            <div className='flex items-center gap-2'>
              <Clock size={16} className='text-[var(--brand-primary)]' />
              <h4 className='font-semibold text-[var(--text-primary)] text-[14px]'>Sessão</h4>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 pl-6'>
              <NumberField
                label='Timeout de inatividade'
                value={settings.sessionTimeoutMinutes}
                onChange={(v) => update({ sessionTimeoutMinutes: v })}
                suffix='minutos'
              />
              <NumberField
                label='Máximo sessões simultâneas'
                value={settings.maxConcurrentSessions}
                onChange={(v) => update({ maxConcurrentSessions: v })}
              />
            </div>
          </div>

          {/* Salvar */}
          <div className='pt-2'>
            <Button variant='primary' size='sm' icon={<Save size={13} />} onClick={handleSave}>
              Salvar
            </Button>
          </div>
        </div>
      )}

      {subTab === 'behavioral' && <BehavioralSecurityTab />}
      {subTab === 'audit' && <AuditLogTab />}
      {subTab === 'backup' && <BackupTab />}
      {subTab === 'blocked' && <BlockedUsersTab />}
      {subTab === 'logs' && <SystemLogsTab />}
    </div>
  )
}
